import React from 'react';

export type TranslationContext = Record<string, unknown>;

export type ServerTranslateFunction = (
    translationKey: string,
    context?: TranslationContext,
) => string;

type ComponentsMap = Record<string, React.ReactElement>;

export type TranslateRenderOptions = {
    html?: boolean;
    components?: ComponentsMap;
};

/**
 * Simplified server-side component interpolation for email templates.
 * Parses translation strings with named XML-style tags like <link>text</link>
 * and replaces them with the provided React components.
 *
 * Example:
 *   Translation: "Visit <link>our website</link> for more info"
 *   Components: { link: <a href="...">...</a> }
 *   Result: React element with link component wrapping "our website"
 *
 * Note: For client-side React components, use Trans from react-i18next instead,
 * which provides more robust component interpolation with the same syntax.
 */
const renderWithComponents = (translated: string, components: ComponentsMap): React.ReactElement => {
    const regex = /<(\/?)([a-zA-Z][a-zA-Z0-9]*)>/g;

    type Node = { name: string; children: Array<string | React.ReactElement> };

    const root: Node = { name: 'root', children: [] };
    const stack: Node[] = [root];

    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(translated))) {
        const [tag, closingSlash, name] = match;
        const current = stack[stack.length - 1];

        const text = translated.slice(lastIndex, match.index);
        if (text) current.children.push(text);

        lastIndex = regex.lastIndex;

        const isClosing = closingSlash === '/';

        if (!isClosing) {
            const component = components[name];

            if (!component) {
                current.children.push(tag);
                continue;
            }

            const node: Node = { name, children: [] };
            stack.push(node);
        } else {
            const node = stack.pop();

            if (!node || node.name === 'root' || node.name !== name) {
                (stack[stack.length - 1] || root).children.push(tag);
                continue;
            }

            const component = components[name];

            if (!component) {
                const plain = `<${name}>${node.children.join('')}</${name}>`;
                (stack[stack.length - 1] || root).children.push(plain);
                continue;
            }

            const wrapped = React.cloneElement(
                component,
                {
                    key: `${name}-${(stack[stack.length - 1] || root).children.length}`,
                },
                node.children,
            );

            (stack[stack.length - 1] || root).children.push(wrapped);
        }
    }

    const remaining = translated.slice(lastIndex);
    if (remaining) {
        (stack[stack.length - 1] || root).children.push(remaining);
    }

    if (root.children.length === 1 && React.isValidElement(root.children[0])) {
        return root.children[0] as React.ReactElement;
    }

    return React.createElement('span', null, root.children);
};

export function translate(
    translator: ServerTranslateFunction | undefined,
    translationKey: string,
    context?: TranslationContext,
): string;

export function translate(
    translator: ServerTranslateFunction | undefined,
    translationKey: string,
    context: TranslationContext | undefined,
    options: TranslateRenderOptions & { html: true },
): React.ReactElement;

export function translate(
    translator: ServerTranslateFunction | undefined,
    translationKey: string,
    context: TranslationContext | undefined,
    options: TranslateRenderOptions & { components: Record<string, React.ReactElement> },
): React.ReactElement;

export function translate(
    translator: ServerTranslateFunction | undefined,
    translationKey: string,
    context: TranslationContext | undefined,
    options: TranslateRenderOptions | undefined,
): string | React.ReactElement;

export function translate(
    translator: ServerTranslateFunction | undefined,
    translationKey: string,
    context?: TranslationContext,
    options?: TranslateRenderOptions,
): string | React.ReactElement {
    let translated = translationKey;

    if (typeof translator === 'function') {
        try {
            translated = translator(translationKey, context);
        } catch (error) {
            // Fall back to key
            translated = translationKey;
        }
    }

    if (options?.components) {
        return renderWithComponents(translated, options.components);
    }

    if (options?.html) {
        return React.createElement('span', {
            className: 'TranslatedString',
            'data-translation-key': translationKey,
            dangerouslySetInnerHTML: { __html: translated },
        } as React.HTMLAttributes<HTMLSpanElement>);
    }

    return translated;
}

type TranslateCountOptions = {
    context?: TranslationContext;
    countOnly?: boolean;
    suffixes?: {
        one?: string;
        other?: string;
    };
};

export const translateCount = (
    translator: ServerTranslateFunction | undefined,
    translationKeyBase: string,
    count: number,
    options: TranslateCountOptions = {},
): string => {
    const { context = {}, countOnly, suffixes = {} } = options;
    const { one: oneSuffix = '_one', other: otherSuffix = '_other' } = suffixes;

    const keySuffix = count === 1 ? oneSuffix : otherSuffix;
    const translationKey = `${translationKeyBase}${keySuffix}`;

    const translationContext = { count, ...context };

    const result = translate(translator, translationKey, translationContext);

    if (countOnly && typeof result === 'string') {
        return result;
    }

    return String(result);
};
